import { useCallback, useEffect, useRef, useState } from 'react';
import { logger } from '@utils/logger';
import { useSetStatusMutation } from '@store/rtk/auth';
import { selectUsers, setUserInfo } from '@store/slices/users/usersSlice';
import { useAppDispatch, useAppSelector } from '@store/hooks';

const events: string[] = [
  'click',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
];

const formatTime = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  const pad = (num: number) => num.toString().padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

const CHANNEL_NAME = 'inactivity-timer-channel';
const MESSAGES = {
  RESET: 'RESET_TIMER',
  LOGOUT: 'LOGOUT_SESSION',
} as const;

export const useCountDown = ({
  onEnd,
  minutes = 30,
  isUserActive = true,
}: {
  onEnd?: () => void;
  minutes?: number;
  isUserActive?: boolean;
}) => {
  const totalDurationInSeconds = minutes * 60;

  const [remainingTimeInSeconds, setRemainingTimeInSeconds] = useState(
    totalDurationInSeconds
  );

  const [setStatus] = useSetStatusMutation();
  const { userInfo } = useAppSelector(selectUsers);
  const dispatch = useAppDispatch();

  const finalTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );

  const channelRef = useRef<BroadcastChannel | null>(null);
  const lastResetTimeRef = useRef<number>(0);
  const onEndRef = useRef(onEnd);
  const fiveMinCalledRef = useRef(false);

  useEffect(() => {
    onEndRef.current = onEnd;
  }, [onEnd]);

  const clearTimers = useCallback(() => {
    if (finalTimeoutRef.current) clearTimeout(finalTimeoutRef.current);
    if (countdownIntervalRef.current)
      clearInterval(countdownIntervalRef.current);

    finalTimeoutRef.current = null;
    countdownIntervalRef.current = null;
  }, []);

  const startTimers = useCallback(() => {
    clearTimers();
    if (!isUserActive) return;

    setRemainingTimeInSeconds(totalDurationInSeconds);
    fiveMinCalledRef.current = false;

    finalTimeoutRef.current = setTimeout(() => {
      channelRef.current?.postMessage(MESSAGES.LOGOUT);
      onEndRef.current?.();
    }, totalDurationInSeconds * 1000);

    countdownIntervalRef.current = setInterval(() => {
      setRemainingTimeInSeconds((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
  }, [isUserActive, totalDurationInSeconds, clearTimers]);

  const resetLogoutTimer = useCallback(() => {
    const now = Date.now();
    if (now - lastResetTimeRef.current < 2000) return;

    lastResetTimeRef.current = now;
    if (userInfo?.status === 'Away') {
      fiveMinCalledRef.current = false;

      setStatus({ status: 'Online' });
      dispatch(setUserInfo({ ...userInfo, status: 'Online' }));
    }

    startTimers();
    channelRef.current?.postMessage(MESSAGES.RESET);
  }, [startTimers, setStatus, userInfo?.status]);

  useEffect(() => {
    const elapsedSeconds = totalDurationInSeconds - remainingTimeInSeconds;

    if (elapsedSeconds === 300 && !fiveMinCalledRef.current) {
      fiveMinCalledRef.current = true;
      const newUserInfo = { ...userInfo, status: 'Away' };
      setStatus({ status: 'Away' });
      dispatch(setUserInfo(newUserInfo));
    }
  }, [remainingTimeInSeconds, totalDurationInSeconds, setStatus]);

  useEffect(() => {
    if (!isUserActive) {
      clearTimers();
      return;
    }

    try {
      channelRef.current = new BroadcastChannel(CHANNEL_NAME);
    } catch {
      logger.warn('BroadcastChannel not supported');
    }

    const handleBroadcastMessage = (event: MessageEvent) => {
      if (event.data === MESSAGES.RESET) {
        startTimers();
      } else if (event.data === MESSAGES.LOGOUT) {
        clearTimers();
        onEndRef.current?.();
      }
    };

    channelRef.current?.addEventListener('message', handleBroadcastMessage);
    events.forEach((event) => window.addEventListener(event, resetLogoutTimer));

    startTimers();

    return () => {
      events.forEach((event) =>
        window.removeEventListener(event, resetLogoutTimer)
      );
      channelRef.current?.removeEventListener(
        'message',
        handleBroadcastMessage
      );
      channelRef.current?.close();
      clearTimers();
    };
  }, [isUserActive, startTimers, resetLogoutTimer, clearTimers]);

  return {
    resetLogoutTimer,
    remainingTime: formatTime(remainingTimeInSeconds),
  };
};
